import { ReactNode, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Send } from "lucide-react";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../api/client";
import * as reportOrdiniApi from "../../api/reportOrdini";
import { AttivitaOrdine, ReportOrdini, RigaOrdine, TipoOrigine } from "../../api/reportOrdini";
import { Table, TableColumn } from "../../components/ui/Table";
import { Button } from "../../components/ui/Button";
import { ExportButton } from "../../components/ui/ExportButton";
import { InviaReportModal } from "../../components/ui/InviaReportModal";
import { SECTION_COLORS, SectionKey, sectionFlatStyle } from "../../utils/sectionColors";

/**
 * REPORT ORDINI — le due domande dell'amministrazione, su una pagina.
 *
 * Solo admin (`report_ordini:read`). Elenca gli ordini OdA/OdV collegati a
 * installazioni, attivazioni, interventi e sopralluoghi: una riga per ORDINE,
 * con le attività che copre una sotto l'altra (issue #211, PR A).
 *
 * ## Le sezioni ci sono sempre tutte
 *
 * Anche vuote. «Nessun OdV da qualificare» è una risposta e va vista: nascondere
 * la sezione vuota lascerebbe chi guarda a chiedersi se la domanda è stata posta.
 * Per lo stesso motivo l'intestazione porta il conteggio — zero incluso.
 *
 * ## Niente ordinamento dalle intestazioni
 *
 * Stessa scelta del Registro attività, e per una ragione diversa ma parallela:
 * qui le righe arrivano già ordinate dalla più VECCHIA, che è l'ordine di lavoro
 * (la fattura che manca da più tempo è quella da sollecitare). Riordinarle per
 * cliente o per numero perderebbe l'unica informazione che l'elenco dà
 * gratuitamente — la priorità. La scelta colonne resta, perché spegnere una
 * colonna non cambia quali righe si vedono.
 */

/** Il colore di sezione per ognuno dei quattro moduli, per i chip del tipo. */
const SEZIONE_PER_TIPO: Record<TipoOrigine, SectionKey> = {
  installazione: "installazioni",
  attivazione: "attivazioni",
  intervento: "interventi",
  sopralluogo: "sopralluoghi",
};

/** Etichetta al singolare: la colonna dice cos'è UNA riga, non il modulo. */
const ETICHETTA_TIPO: Record<TipoOrigine, string> = {
  installazione: "Installazione",
  attivazione: "Attivazione",
  intervento: "Intervento",
  sopralluogo: "Sopralluogo",
};

/**
 * La pagina che possiede il pannello di dettaglio di ciascun tipo, con `?open=`.
 *
 * Stessa convenzione e stessa mappa del Calendario unificato: il clic porta al
 * pannello ESISTENTE di quel modulo, con i permessi già in vigore là.
 *
 * Un limite noto della convenzione, che vale anche qui: la pagina apre il
 * pannello solo se quel record è fra quelli che ha caricato. Un record fuori
 * dall'elenco corrente (filtro attivo, o oltre il taglio) lascia la pagina aperta
 * sull'elenco senza pannello. È il comportamento che i link del Calendario e
 * della Vista Sede hanno già: non lo si peggiora e non lo si risolve qui.
 */
const ROTTA_PER_TIPO: Record<TipoOrigine, string> = {
  installazione: "/schedule",
  attivazione: "/activations",
  intervento: "/interventions",
  sopralluogo: "/sopralluoghi",
};

/**
 * Il nome del file scaricato, con la data di oggi: il report e' una fotografia
 * di adesso, e due scarichi di giorni diversi non devono chiamarsi uguale nella
 * cartella dei download. Ricalcolato dal client, ma il server ne mette uno
 * suo identico nell'header — vedi `nomeFileOrdini` in `report/excelOrdini.ts`.
 */
function nomeFileOggi(): string {
  return `report-ordini-${new Date().toISOString().slice(0, 10)}.xlsx`;
}

function formattaData(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("it-IT");
}

/**
 * Una colonna per attività: un valore per ciascuna attività dell'ordine, uno
 * sotto l'altro e alla stessa altezza in tutte le colonne, così la terza riga di
 * «Cliente» è il cliente della terza attività. Con una sola attività — quasi
 * tutti gli ordini — è la cella di sempre.
 */
function PerAttivita({ righe, valore }: { righe: AttivitaOrdine[]; valore: (a: AttivitaOrdine) => ReactNode }) {
  return (
    <div className="flex flex-col divide-y divide-dashed divide-black/10">
      {righe.map((a) => (
        <div key={`${a.tipo}-${a.id}`} className="flex min-h-[3.25rem] flex-col justify-center py-1">
          {valore(a)}
        </div>
      ))}
    </div>
  );
}

/**
 * Le colonne, identiche nelle cinque sezioni a meno dell'intestazione del
 * numero: nella prima è un OdA, nelle altre un OdV. Una funzione e non cinque
 * elenchi copiati — divergerebbero alla prima correzione, ed è il difetto che
 * l'elenco unico esiste per evitare.
 */
function colonne(intestazioneNumero: string): TableColumn<RigaOrdine>[] {
  // Tutte le celle allineate in ALTO (il `Table` le centra): con le attività
  // una sotto l'altra, una cella più alta delle altre — lo stato con il motivo
  // sotto — centrata sposterebbe le sue righe rispetto a quelle delle colonne
  // accanto, e il terzo stato non starebbe più accanto alla terza attività.
  return colonneBase(intestazioneNumero).map((c) => ({ ...c, className: "!align-top" }));
}

function colonneBase(intestazioneNumero: string): TableColumn<RigaOrdine>[] {
  return [
    {
      key: "numero",
      header: intestazioneNumero,
      sempreVisibile: true,
      render: (r) => (
        <div className="flex flex-col gap-0.5">
          <span className="font-mono text-xs font-semibold">{r.numero}</span>
          {/* Detto in chiaro: la fattura dell'ordine vale per tutte. */}
          {r.attivita.length > 1 && <span className="text-xs text-ink/50">{r.attivita.length} attività</span>}
        </div>
      ),
    },
    {
      key: "tipo",
      header: "Da",
      sempreVisibile: true,
      render: (r) => (
        <PerAttivita
          righe={r.attivita}
          valore={(a) => (
            <Link
              to={`${ROTTA_PER_TIPO[a.tipo]}?open=${a.id}`}
              className="inline-flex flex-col gap-0.5 hover:underline"
              title={`Apri ${ETICHETTA_TIPO[a.tipo].toLowerCase()} in ${ROTTA_PER_TIPO[a.tipo]}`}
            >
              <span
                className="inline-block w-fit rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
                style={sectionFlatStyle(SEZIONE_PER_TIPO[a.tipo])}
              >
                {ETICHETTA_TIPO[a.tipo]}
              </span>
              {/* L'etichetta del record sotto il chip: senza, due attività dello
                  stesso tipo e dello stesso cliente sono indistinguibili, e il
                  link non dice dove porta. */}
              {a.etichetta && <span className="text-xs text-ink/60">{a.etichetta}</span>}
            </Link>
          )}
        />
      ),
    },
    {
      key: "cliente",
      header: "Cliente",
      render: (r) => (
        <PerAttivita righe={r.attivita} valore={(a) => a.cliente ?? <span className="text-ink/40">non collegato</span>} />
      ),
    },
    {
      key: "sede",
      header: "Sede",
      render: (r) => <PerAttivita righe={r.attivita} valore={(a) => a.sede ?? <span className="text-ink/40">—</span>} />,
    },
    {
      key: "stato",
      header: "Stato",
      // Scritto alla lettera com'e' in tabella, senza tradurlo e senza colorarlo
      // come i badge di stato degli altri elenchi. Qui convivono quattro
      // vocabolari diversi — e quello degli interventi arriva da Odoo — quindi
      // un badge colorato accosterebbe stati che non sono la stessa cosa.
      // Il motivo, quando c'è, è dell'ORDINE e sta sotto gli stati delle sue
      // attività: si legge insieme alla ragione per cui l'ordine è qui.
      render: (r) => (
        <div className="flex flex-col gap-0.5">
          <PerAttivita righe={r.attivita} valore={(a) => a.stato ?? <span className="text-ink/40">—</span>} />
          {r.motivo && <span className="text-xs font-medium text-[#c2410c]">{r.motivo}</span>}
        </div>
      ),
    },
    {
      key: "data",
      header: "Data",
      sempreVisibile: true,
      render: (r) => (
        <PerAttivita
          righe={r.attivita}
          valore={(a) => <span className={a.data ? undefined : "text-ink/40"}>{formattaData(a.data)}</span>}
        />
      ),
    },
  ];
}

/**
 * Una sezione: titolo, conteggio, spiegazione della condizione, tabella.
 *
 * La spiegazione non è decorativa. Le tre sezioni OdV si distinguono per una
 * condizione su un campo a tre stati, e senza dirla per iscritto «Da qualificare»
 * si legge come un errore invece che come una domanda aperta.
 */
function Sezione({
  titolo,
  spiegazione,
  righe,
  intestazioneNumero,
  idTabella,
  vuota,
  accento,
}: {
  titolo: string;
  spiegazione: string;
  righe: RigaOrdine[];
  intestazioneNumero: string;
  idTabella: string;
  vuota: string;
  /** Colore della barretta a sinistra del titolo — distingue OdA da OdV. */
  accento: string;
}) {
  return (
    <section className="mb-8">
      <div className="mb-3 flex items-baseline gap-2 border-l-4 pl-3" style={{ borderColor: accento }}>
        <h2 className="text-lg font-bold text-ink">{titolo}</h2>
        <span className="text-sm font-semibold text-ink/50">
          {righe.length} {righe.length === 1 ? "ordine" : "ordini"}
        </span>
      </div>
      <p className="mb-3 pl-4 text-sm text-ink/50">{spiegazione}</p>
      <Table
        columns={colonne(intestazioneNumero)}
        sceltaColonne={`report-ordini-${idTabella}`}
        data={righe}
        rowKey={(r) => r.ordine_id}
        emptyMessage={vuota}
      />
    </section>
  );
}

export default function OrdiniReport() {
  const { showToast } = useToast();
  const [dati, setDati] = useState<ReportOrdini | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [invioAperto, setInvioAperto] = useState(false);

  async function carica() {
    setIsLoading(true);
    try {
      setDati(await reportOrdiniApi.getReportOrdini());
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare il report ordini."), "error");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    carica();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Il conteggio complessivo di cose da fare: le due sezioni in cui un ordine
  // rappresenta un'azione mancante (una fattura da ricevere, una da emettere).
  // "Non da fatturare" e "Da qualificare" restano fuori: la prima non chiede
  // niente, la seconda chiede una decisione e non una fattura.
  const daFare = dati ? dati.oda.length + dati.odv.da_fatturare.length : 0;

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-ink">Report Ordini</h1>
          <p className="mt-1 max-w-3xl text-sm text-ink/50">
            Gli OdA e gli OdV collegati a installazioni, attivazioni, interventi e sopralluoghi, messi in fila su
            tutti i clienti insieme: una riga per ordine, con le attività che copre. Gli ordini più vecchi stanno in
            cima: sono quelli da sollecitare per primi. Visibile solo agli admin.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="secondary" size="sm" loading={isLoading} onClick={carica}>
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Aggiorna
          </Button>
          {/* Nessun filtro nel pannello: il report non ne ha a schermo, e un
              export che filtrasse dove la pagina non filtra darebbe un file
              diverso da quello che si sta guardando. Senza `fields`, il
              componente scarica direttamente al clic. */}
          <ExportButton endpoint="/report-ordini/export" filename={nomeFileOggi()} />
          <Button type="button" variant="secondary" size="sm" onClick={() => setInvioAperto(true)}>
            <Send className="h-4 w-4" aria-hidden="true" />
            Invia via email
          </Button>
        </div>
      </header>

      {!isLoading && dati && (
        <p className="mb-6 rounded-lg bg-primary/5 px-4 py-3 text-sm text-ink/70">
          {daFare === 0 ? (
            <>Nessuna fattura da ricevere o da emettere: le sezioni qui sotto dicono dove sta il resto.</>
          ) : (
            <>
              <span className="font-semibold text-ink">{daFare}</span> {daFare === 1 ? "ordine chiede" : "ordini chiedono"}{" "}
              una fattura —{" "}
              {dati.oda.length} da ricevere dai fornitori, {dati.odv.da_fatturare.length} da emettere ai clienti.
            </>
          )}
        </p>
      )}

      {isLoading && <p className="text-sm text-ink/50">Caricamento…</p>}

      {dati && (
        <>
          <Sezione
            titolo="OdA da fatturare"
            spiegazione="Ordini al fornitore per cui la fattura del fornitore non è ancora arrivata. Spuntare «Fattura del fornitore ricevuta» su una qualunque delle sue attività lo toglie da qui: la fattura è dell'ordine."
            righe={dati.oda}
            intestazioneNumero="Numero OdA"
            idTabella="oda"
            vuota="Nessun OdA in attesa di fattura dal fornitore."
            accento={SECTION_COLORS.installazioni.base}
          />

          <Sezione
            titolo="OdV da fatturare"
            spiegazione="Ordini di vendita dichiarati NON in garanzia, non ancora fatturati al cliente, con tutte le attività a uno stadio fatturabile."
            righe={dati.odv.da_fatturare}
            intestazioneNumero="Numero OdV"
            idTabella="odv-da-fatturare"
            vuota="Nessun OdV da fatturare."
            accento={SECTION_COLORS.attivazioni.base}
          />

          {/* Subito sotto «da fatturare» perché è la stessa domanda con una
              risposta diversa: la fattura ci andrebbe, ma non adesso. Sopra le
              altre due, che parlano di righe su cui NON si aspetta niente. */}
          <Sezione
            titolo="OdV non ancora fatturabili"
            spiegazione="Ordini di vendita non in garanzia e non ancora fatturati, con almeno un'attività che non è a uno stadio fatturabile: un ordine si fattura quando lo sono tutte. Non serve una decisione dell'amministrazione: serve che il lavoro avanzi. Installazioni: fatturabili «In corso» ed «Effettuata». Sopralluoghi: tutti tranne «Da revisionare». Attivazioni: quelle segnate come effettuate. Interventi: quelli con una data di completamento; gli annullati solo se l'annullamento è stato deciso a carico del cliente, anche senza data. Gli annullati decisi non fatturabili o ancora da decidere stanno nelle sezioni sotto."
            righe={dati.odv.non_ancora_fatturabili}
            intestazioneNumero="Numero OdV"
            idTabella="odv-non-ancora-fatturabili"
            vuota="Nessun OdV in attesa che il lavoro arrivi a uno stadio fatturabile."
            accento={SECTION_COLORS.installazioni.base}
          />

          <Sezione
            titolo="OdV non da fatturare (in garanzia o annullati)"
            spiegazione="Ordini di vendita dichiarati in garanzia, e interventi annullati su cui è stato deciso che l'annullamento non si fattura: non si fatturano. Restano elencati per poterlo dire — e perché un OdV in garanzia che risultasse anche fatturato è una contraddizione che si vede solo guardandola."
            righe={dati.odv.non_da_fatturare}
            intestazioneNumero="Numero OdV"
            idTabella="odv-garanzia"
            vuota="Nessun OdV in garanzia né annullato."
            accento={SECTION_COLORS.sopralluoghi.base}
          />

          <Sezione
            titolo="OdV da qualificare"
            spiegazione="Domanda aperta: garanzia non dichiarata o annullamento da decidere. Ordini di vendita su cui nessuno ha ancora indicato se sono in garanzia, e interventi annullati su cui nessuno ha ancora deciso se l'annullamento si fattura — anche quando l'OdV è in garanzia o già fatturato, e allora il motivo è scritto sotto lo stato. Non sono «da fatturare»: finché la domanda resta aperta non si sa in quale delle sezioni sopra dovrebbero stare. Qui finiscono anche gli annullati decisi «da fatturare» su un OdV dichiarato in garanzia, e gli OdV che mescolano interventi annullati non fatturabili ad altre attività: i dati si contraddicono, e il motivo è scritto sotto lo stato."
            righe={dati.odv.da_qualificare}
            intestazioneNumero="Numero OdV"
            idTabella="odv-da-qualificare"
            vuota="Nessun OdV da qualificare: su tutti è stata dichiarata la garanzia, e nessun annullamento è da decidere."
            accento={SECTION_COLORS.interventi.base}
          />
        </>
      )}

      {/* Lo stesso pannello di invio degli altri documenti dell'app, e lo stesso
          servizio dietro (`inviaReport` -> Microsoft Graph, con la riga nel
          registro attivita'). Due differenze, entrambe volute:

          - `caricaDestinatari` non propone niente. Gli altri invii partono da un
            record e possono pescare l'indirizzo della sede o del cliente; questo
            aggrega quattro tabelle su tutti i clienti, e non esiste "l'indirizzo
            del report". Il campo libero e' la strada, non il ripiego — il
            pannello e' gia' costruito per quel caso.
          - niente `scarica`: il bottone di scarico sta nell'intestazione, dove
            serve anche a chi non vuole mandare niente a nessuno. */}
      <InviaReportModal
        open={invioAperto}
        onClose={() => setInvioAperto(false)}
        titolo="Invia il Report Ordini"
        nomeAllegato={nomeFileOggi()}
        messaggioSenzaProposte="Questo report non parte da un record dell'anagrafica: l'indirizzo si scrive qui sotto."
        caricaDestinatari={async () => []}
        invia={(destinatari, nota) => reportOrdiniApi.inviaReportOrdini(destinatari, nota)}
      />
    </div>
  );
}
